import { createContext, useContext, useEffect, useState } from "react";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { auth, authPersistenceReady, db, getAppCheckHeaders } from "../firebase";

import { clearOrderListCache } from "../services/orderListCache";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [role, setRole] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  async function loadUserProfile(currentUser) {
    await getAppCheckHeaders();

    const userRef = doc(db, "users", currentUser.uid);
    const userSnap = await getDoc(userRef);
    const profileData = userSnap.exists() ? userSnap.data() : null;

    setProfile(profileData);
    setRole(profileData?.role || null);

    return profileData;
  }

  useEffect(() => {
    let unsubscribe = () => {};
    let cancelled = false;

    authPersistenceReady.finally(() => {
      if (cancelled) return;

      unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
        clearOrderListCache();
        setLoading(true);

        if (!currentUser) {
          setUser(null);
          setRole(null);
          setProfile(null);
          setLoading(false);
          return;
        }

        setUser({
          uid: currentUser.uid,
          email: currentUser.email,
          emailVerified: currentUser.emailVerified
        });

        try {
          await loadUserProfile(currentUser);
        } catch (error) {
          console.error(error);
          setRole(null);
          setProfile(null);
        } finally {
          setLoading(false);
        }
      });
    });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, []);

  async function logout() {
    await signOut(auth);
  }

  async function refreshProfile() {
    if (!auth.currentUser) return null;

    return loadUserProfile(auth.currentUser);
  }

  const value = {
    user,
    role,
    profile,
    loading,
    isAdmin: role === "admin",
    refreshProfile,
    logout
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
